import {
	ReactElement,
	useState,
	useEffect
} from "react";
import { Spinner, NoResults } from "client/Components";
import { DataSourceSortableState } from "client/Data/sorting";
import { useForceRerender } from "client/Hooks";
import { isNil } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { createFilters, createOrder } from "../Overview/useDataSource";
import {
	AnnouncementDetailsViewModel,
	AnnouncementPreviewItemModel,
	AnnouncementPagingInformationModel,
	AnnouncementSortingInformationModel,
	getRelevantItems
} from "client/Api/Hub/Announcements/announcementViewer";
import styles from "./Details.module.less";
import { List } from "../Overview/List/List";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	ActiveFilter,
	HubItemIsNewFilter,
	hubItemIsNewToFlag
} from "../../common";

type RelevantAnnouncementsSectionProps = {
	item: AnnouncementDetailsViewModel | null;
	viewsCount: number | undefined;
	activeFilter: ActiveFilter | null;
	selectedCategoryId: number | null;
	searchTerm: VersionedValue<string>;
	sorting: DataSourceSortableState<AnnouncementPreviewItemModel>;
	onNavigateToItem(id: number): void;
	timezone: TimeZone;
	isNewFilter: HubItemIsNewFilter;
};

export const RelevantAnnouncementsSection = (props: RelevantAnnouncementsSectionProps): ReactElement => {
	const {
		item,
		viewsCount,
		activeFilter,
		selectedCategoryId,
		searchTerm,
		sorting,
		onNavigateToItem,
		timezone,
		isNewFilter
	} = props;

	const [state, setState] = useState<LoadingState>({
		kind: "loading"
	});

	const [retryHandler, itemsVersion] = useForceRerender();

	useEffect(
		(): (() => void) | undefined => {
			if (!item || isNil(viewsCount)) {
				return;
			}

			hasImplicitDependencyOn(itemsVersion);

			const load = async (signal: AbortSignal): Promise<void> => {
				try {
					setState({
						kind: "loading"
					});

					const pagingInformation: AnnouncementPagingInformationModel = {
						id: item.announcementId,
						startTime: item.publicationStart,
						viewsCount: viewsCount,
						pinDate: item.pinDate
					};

					const [authorId, tagId] = createFilters(activeFilter);

					const sortOrder: AnnouncementSortingInformationModel | null = createOrder(sorting);

					const items: readonly AnnouncementPreviewItemModel[] = await getRelevantItems(
						item.announcementId,
						pagingInformation,
						selectedCategoryId,
						authorId,
						tagId,
						searchTerm.value,
						sortOrder,
						hubItemIsNewToFlag[isNewFilter],
						signal
					);

					setState({
						kind: "success",
						items
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					console.error(e);

					setState({
						kind: "error"
					});
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[
			activeFilter,
			item,
			itemsVersion,
			searchTerm.value,
			selectedCategoryId,
			sorting,
			viewsCount,
			isNewFilter
		]
	);

	switch (state.kind) {
		case "loading":
			return(
				<div className={styles.spinnerBlock}>
					<Spinner />
				</div>
			);
		case "error":
			return(
				<NoResults
					trackingId="relevant-items-failure"
					icon="bullhorn"
					title={localize("_UnableToRetrieveItems")}
					message={localize("_WeWereUnableToRetrieveTheItems")}
					onRetry={retryHandler}
				/>
			);
		case "success":
			if (state.items.length === 0) {
				return (
					<NoResults
						trackingId="no-relevant-items"
						icon="bullhorn"
						title={localize("_NoAnnouncementsConfiguredMessage")}
						message={localize("_ThereAreCurrentlyNoItemsToShowHere")}
					/>
				);
			}

			return (
				<List
					items={state.items}
					trackingId="announcement-list-relevant"
					onNavigateToItem={onNavigateToItem}
					timezone={timezone}
				/>
			);
		default:
			never(state);
	}

};

RelevantAnnouncementsSection.displayName = "RelevantAnnouncementsSection";

type LoadingState =
	| { kind: "loading"; }
	| { kind: "error"; }
	| { kind: "success"; items: readonly AnnouncementPreviewItemModel[]; };